const YOUTUBE_VIDEO_ID_PATTERN =
  /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|watch)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\r\n]{11})/;

export const getYouTubeVideoId = (url: string | null): string | null => {
  if (typeof url !== 'string' || !url) {
    return null;
  }

  const match = url.match(YOUTUBE_VIDEO_ID_PATTERN);
  return match ? match[1] : null;
};

export const getYouTubeThumbnail = (url: string): string | null => {
  const videoId = getYouTubeVideoId(url);
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
  return null;
};

export const getYouTubeEmbedUrl = (url: string): string => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) {
    return url;
  }

  const params = new URLSearchParams({ autoplay: '1', enablejsapi: '1' });
  const origin = typeof window === 'undefined' ? undefined : window.location?.origin;
  if (origin) {
    params.set('origin', origin);
  }

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

const YOUTUBE_API_SCRIPT_ID = 'youtube-api-script';

type YouTubeApi = { ready: (callback: () => void) => void; Player: unknown };

const getYouTubeApi = (): YouTubeApi | undefined =>
  typeof window === 'undefined' ? undefined : (window as unknown as { YT?: YouTubeApi }).YT;

const waitingSubscribers: (() => void)[] = [];

const drainSubscribers = (): void => {
  waitingSubscribers.splice(0).forEach((subscriber) => {
    try {
      subscriber();
    } catch {}
  });
};

const ensureApiScript = (): void => {
  if (document.getElementById(YOUTUBE_API_SCRIPT_ID)) {
    return;
  }
  const tag = document.createElement('script');
  tag.id = YOUTUBE_API_SCRIPT_ID;
  tag.src = 'https://www.youtube.com/iframe_api';
  tag.onload = () => getYouTubeApi()?.ready(drainSubscribers);
  const firstScriptTag = document.getElementsByTagName('script')[0];
  if (firstScriptTag?.parentNode) {
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  } else {
    document.head.appendChild(tag);
  }
};

export const whenYouTubeApiReady = (callback: () => void): (() => void) => {
  let cancelled = false;
  const guarded = () => {
    if (!cancelled) {
      callback();
    }
  };
  const unsubscribe = () => {
    cancelled = true;
  };

  const api = getYouTubeApi();
  if (api?.ready) {
    api.ready(guarded);
    return unsubscribe;
  }

  waitingSubscribers.push(guarded);
  ensureApiScript();
  return unsubscribe;
};

export const resetYouTubeApiSubscribers = (): void => {
  waitingSubscribers.length = 0;
};
